import { ColumnMapping, DuplicatePolicy, ResolveResult, ResolvedRow } from "./types";

export type PreparedRow = {
    /** Motivo por el que la fila no se puede importar. `null` si esta bien. */
    error: string | null;
    /** Avisos que se muestran en la vista previa sin bloquear la importacion. */
    messages: string[];
};

export type ResolveRowsConfig<TData, TExisting> = {
    dataRows: string[][];
    mapping: ColumnMapping;
    policy: DuplicatePolicy;
    existing: TExisting[];

    /** Convierte una fila cruda del archivo en los atributos del modelo. */
    mapRow: (row: string[], mapping: ColumnMapping) => { data: TData; lookupName: string };
    /**
     * Valida la fila y le aplica los defaults del modelo. Se llama despues de resolver la
     * entidad relacionada, para que los defaults puedan depender de ella.
     */
    prepare: (data: TData, lookupName: string, rowIndex: number) => PreparedRow;

    /** Llaves con las que se busca la fila entre lo que ya existe, en orden de prioridad. */
    duplicateKeys: (data: TData) => string[];
    /** Llaves de un registro existente del tenant. */
    existingKeys: (item: TExisting) => string[];
    idOf: (item: TExisting) => string | undefined;

    /** Codigo consecutivo: lectura, escritura y valor actual de los registros existentes. */
    codeOf: (data: TData) => string;
    setCode: (data: TData, code: string) => void;
    existingCodeOf: (item: TExisting) => string;
    /** Ajustes extra cuando un duplicado se importa igual como registro nuevo. */
    onDuplicateAsNew?: (data: TData, messages: string[]) => void;

    /** Etiqueta del registro en los mensajes: "cliente", "producto". */
    entitySingular: string;
};

/**
 * Siguiente codigo consecutivo disponible.
 *
 * Se calcula una sola vez y se incrementa en memoria: `GenericModelApi.getNextCode`
 * relee todos los registros en cada llamada y aqui hay cientos de filas.
 */
export const nextAvailableCode = (codes: Array<string | number | null | undefined>): number =>
    codes.reduce<number>((max, raw) => {
        const code = Number(raw);
        return Number.isNaN(code) ? max : Math.max(max, code);
    }, 0) + 1;

/**
 * Transforma las filas del archivo en registros listos para importar: normaliza los valores,
 * detecta duplicados contra lo que ya existe en el tenant y asigna los codigos que falten.
 *
 * Todo pasa en el navegador para que la vista previa sea fiel a lo que se va a escribir;
 * la lambda recibe filas ya resueltas con una accion explicita.
 */
export const resolveRows = <TData, TExisting>(
    config: ResolveRowsConfig<TData, TExisting>
): ResolveResult<TData> => {
    const {
        dataRows,
        mapping,
        policy,
        existing,
        mapRow,
        prepare,
        duplicateKeys,
        existingKeys,
        idOf,
        codeOf,
        setCode,
        existingCodeOf,
        onDuplicateAsNew,
        entitySingular,
    } = config;

    const byKey = new Map<string, TExisting>();
    const usedCodes = new Set<string>();

    existing.forEach((item) => {
        existingKeys(item).forEach((key) => {
            if (key && !byKey.has(key)) byKey.set(key, item);
        });

        const code = `${existingCodeOf(item) ?? ""}`.trim();
        if (code) usedCodes.add(code);
    });

    let nextCode = nextAvailableCode(existing.map(existingCodeOf));

    const takeNextCode = (): string => {
        while (usedCodes.has(`${nextCode}`)) nextCode += 1;
        const code = `${nextCode}`;
        usedCodes.add(code);
        nextCode += 1;
        return code;
    };

    /**
     * Deja el registro con un codigo unico: respeta el del archivo si esta libre, y si no
     * (o si no viene) asigna el siguiente consecutivo. El codigo es obligatorio en los
     * modelos y no puede repetirse dentro del tenant.
     */
    const assignCode = (data: TData, messages: string[]): void => {
        const fileCode = `${codeOf(data) ?? ""}`.trim();

        if (fileCode && !usedCodes.has(fileCode)) {
            setCode(data, fileCode);
            usedCodes.add(fileCode);
            return;
        }

        const code = takeNextCode();
        setCode(data, code);
        if (fileCode) {
            messages.push(`El codigo ${fileCode} ya estaba en uso: se asigna el ${code}`);
        }
    };

    const rows = dataRows.map<ResolvedRow<TData>>((row, rowIndex) => {
        const { data, lookupName } = mapRow(row, mapping);
        const prepared = prepare(data, lookupName, rowIndex);
        const messages = [...prepared.messages];

        if (prepared.error) {
            return { rowIndex, data, lookupName, status: "error", messages: [prepared.error], action: "skip" };
        }

        const existingMatch = duplicateKeys(data)
            .map((key) => (key ? byKey.get(key) : undefined))
            .find(Boolean);

        if (existingMatch) {
            const existingId = idOf(existingMatch);

            if (policy === "skip") {
                return {
                    rowIndex,
                    data,
                    lookupName,
                    status: "duplicate",
                    messages: [...messages, "Ya existe: se omite"],
                    existingId,
                    action: "skip",
                };
            }

            if (policy === "update") {
                return {
                    rowIndex,
                    data,
                    lookupName,
                    status: "duplicate",
                    messages: [...messages, "Ya existe: se actualiza"],
                    existingId,
                    action: "update",
                };
            }

            // "create": se importa igual, pero el codigo no se puede repetir.
            messages.push(`Ya existe: se importa como ${entitySingular} nuevo`);
            onDuplicateAsNew?.(data, messages);
            assignCode(data, messages);

            return { rowIndex, data, lookupName, status: "warning", messages, existingId, action: "create" };
        }

        assignCode(data, messages);

        return {
            rowIndex,
            data,
            lookupName,
            status: messages.length ? "warning" : "new",
            messages,
            action: "create",
        };
    });

    return { rows };
};

/** Nombres de entidad relacionada que el archivo menciona y todavia no existen, sin repetir. */
export const collectMissingLookupNames = <TData>(
    rows: Array<ResolvedRow<TData>>,
    isResolved: (row: ResolvedRow<TData>) => boolean
): string[] => {
    const seen = new Set<string>();
    const names: string[] = [];

    rows.forEach((row) => {
        const name = row.lookupName.trim();
        if (!name || isResolved(row)) return;

        const key = name.toUpperCase();
        if (seen.has(key)) return;
        seen.add(key);
        names.push(name);
    });

    return names;
};
